import { useMutation, useQuery, useQueryClient } from "react-query";
import { adyenAccountHoldersHttp } from "./adyen-account-holders-http";

export const cacheKeys = {
  adyenAccountHolders: "adyenAccountHolders",
  adyenAccountHolder: "adyenAccountHolder",
  adyenAccountHolderVerificationErrors: "adyenAccountHolderVerificationErrors",
  adyenAccountHolderOnboardingLink: "adyenAccountHolderOnboardingLink",
  adyenAccountHolderCapabilities: "adyenAccountHolderCapabilities",
  adyenAccountHolderBusinessLines: "adyenAccountHolderBusinessLines",
  adyenAccountHolderBusinessLine: "adyenAccountHolderBusinessLine",
  adyenAccountHolderBusinessLinesIndustryCodes:
    "adyenAccountHolderBusinessLinesIndustryCodes",
  adyenAccountHolderStores: "adyenAccountHolderStores",
  adyenAccountHolderStore: "adyenAccountHolderStore",
  adyenBalanceAccounts: "adyenBalanceAccounts",
  adyenBalanceAccount: "adyenBalanceAccount",
  adyenSplitConfigurations: "adyenSplitConfigurations",
  adyenSplitConfiguration: "adyenSplitConfiguration",
  adyenPaymentInstruments: "adyenPaymentInstruments",
};

export const useGetAdyenAccountHolders = () => {
  return useQuery(
    [cacheKeys.adyenAccountHolders],
    () => adyenAccountHoldersHttp.getAdyenAccountHolders(),
    { refetchOnWindowFocus: false }
  );
};

export const useGetAdyenAccountHolder = (id?: string) => {
  return useQuery(
    [cacheKeys.adyenAccountHolder, id],
    () => adyenAccountHoldersHttp.getAdyenAccountHolder(id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useCreateAdyenAccountHolder = () => {
  const queryClient = useQueryClient();

  return useMutation(adyenAccountHoldersHttp.createAdyenAccountHolder, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.adyenAccountHolders]);
    },
  });
};

export const useUpdateAdyenAccountHolder = (id: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation(adyenAccountHoldersHttp.updateAdyenAccountHolder, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.adyenAccountHolders]);
      if (id) {
        queryClient.refetchQueries([cacheKeys.adyenAccountHolder, id]);
      }
    },
  });
};

export const useGetAdyenAccountHolderVerificationErrors = (
  id?: string,
  enabled?: boolean
) => {
  return useQuery(
    [cacheKeys.adyenAccountHolderVerificationErrors, id],
    () => adyenAccountHoldersHttp.getVerificationErrors(id),
    { enabled }
  );
};

export const useGetAdyenAccountHolderOnboardingLink = (
  id?: string,
  enabled?: boolean
) => {
  return useQuery(
    [cacheKeys.adyenAccountHolderOnboardingLink, id],
    () => adyenAccountHoldersHttp.getOnboardingLink(id),
    { enabled }
  );
};

export const useGetAdyenAccountHolderCapabilities = (
  id?: string,
  enabled?: boolean
) => {
  return useQuery(
    [cacheKeys.adyenAccountHolderCapabilities, id],
    () => adyenAccountHoldersHttp.getCapabilities(id),
    { enabled }
  );
};

export const useGetAdyenAccountHolderBusinessLines = (
  id?: string,
  enabled?: boolean
) => {
  return useQuery(
    [cacheKeys.adyenAccountHolderBusinessLines, id],
    () => adyenAccountHoldersHttp.getBusinessLines(id),
    { enabled }
  );
};

export const useGetAdyenAccountHolderBusinessLine = (
  id?: string,
  businessLineId?: string,
  enabled?: boolean
) => {
  return useQuery(
    [cacheKeys.adyenAccountHolderBusinessLine, id, businessLineId],
    () => adyenAccountHoldersHttp.getBusinessLine(id, businessLineId),
    { enabled }
  );
};

export const useCreateAdyenAccountHolderBusinessLine = (
  accountHolderId?: string
) => {
  const queryClient = useQueryClient();

  return useMutation(adyenAccountHoldersHttp.createBusinessLine, {
    onSuccess: async () => {
      if (accountHolderId) {
        queryClient.refetchQueries([
          cacheKeys.adyenAccountHolderBusinessLines,
          accountHolderId,
        ]);
      }
    },
  });
};

export const useUpdateAdyenAccountHolderBusinessLine = (
  accountHolderId?: string,
  businessLineId?: string
) => {
  const queryClient = useQueryClient();

  return useMutation(adyenAccountHoldersHttp.updateBusinessLine, {
    onSuccess: async () => {
      if (accountHolderId) {
        queryClient.refetchQueries([
          cacheKeys.adyenAccountHolderBusinessLines,
          accountHolderId,
        ]);
      }
      if (businessLineId) {
        queryClient.refetchQueries([
          cacheKeys.adyenAccountHolderBusinessLine,
          accountHolderId,
        ]);
      }
    },
  });
};

export const useDeleteAdyenAccountHolderBusinessLine = (
  accountHolderId?: string
) => {
  const queryClient = useQueryClient();

  return useMutation(adyenAccountHoldersHttp.deleteBusinessLine, {
    onSuccess: async () => {
      if (accountHolderId) {
        queryClient.refetchQueries([
          cacheKeys.adyenAccountHolderBusinessLines,
          accountHolderId,
        ]);
      }
    },
  });
};

export const useGetBusinessLineIndustryCodes = () => {
  return useQuery(
    [cacheKeys.adyenAccountHolderBusinessLinesIndustryCodes],
    () => adyenAccountHoldersHttp.getBusinessLinesIndustryCodes(),
    { refetchOnWindowFocus: false }
  );
};

export const usePushAdyenAccountHolderToAdyen = (id?: string) => {
  const queryClient = useQueryClient();

  return useMutation(adyenAccountHoldersHttp.pushAdyenAccountHolderToAdyen, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.adyenAccountHolders]);
    },
  });
};

export const useGetAdyenAccountHolderStores = (
  accountHolderId?: string,
  organizationId?: string
) => {
  return useQuery(
    [cacheKeys.adyenAccountHolderStores, accountHolderId, organizationId],
    () =>
      adyenAccountHoldersHttp.getStores({ accountHolderId, organizationId }),
    { enabled: true }
  );
};

export const useGetAdyenAccountHolderStore = (id?: string) => {
  return useQuery(
    [cacheKeys.adyenAccountHolderStore, id],
    () => adyenAccountHoldersHttp.getStore(id),
    { enabled: !!id }
  );
};

export const useCreateAdyenAccountHolderStore = () => {
  const queryClient = useQueryClient();

  return useMutation(adyenAccountHoldersHttp.createStore, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.adyenAccountHolderStores]);
    },
  });
};

export const useUpdateAdyenAccountHolderStore = (storeId?: string) => {
  const queryClient = useQueryClient();

  return useMutation(adyenAccountHoldersHttp.updateStore, {
    onSuccess: async () => {
      if (storeId) {
        queryClient.refetchQueries([
          cacheKeys.adyenAccountHolderStore,
          storeId,
        ]);
      }
    },
  });
};

export const usePushAdyenStoreToAdyen = (id?: string) => {
  const queryClient = useQueryClient();

  return useMutation(adyenAccountHoldersHttp.pushAdyenStoreToAdyen, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.adyenAccountHolderStores]);
      if (id) {
        queryClient.refetchQueries([cacheKeys.adyenAccountHolderStore, id]);
      }
    },
  });
};

export const useGetBalanceAccounts = (id?: string) => {
  return useQuery(
    [cacheKeys.adyenBalanceAccounts, id],
    () => adyenAccountHoldersHttp.getBalanceAccounts(id),
    { enabled: !!id }
  );
};

export const useGetBalanceAccount = (id?: string) => {
  return useQuery(
    [cacheKeys.adyenBalanceAccount, id],
    () => adyenAccountHoldersHttp.getBalanceAccount(id),
    { enabled: !!id }
  );
};

export const useCreateBalanceAccount = () => {
  const queryClient = useQueryClient();

  return useMutation(adyenAccountHoldersHttp.createBalanceAccount, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.adyenAccountHolderStores]);
    },
  });
};

export const useGetSplitConfigurations = () => {
  return useQuery([cacheKeys.adyenSplitConfigurations], () =>
    adyenAccountHoldersHttp.getSplitConfigurations()
  );
};

export const useGetSplitConfiguration = (id?: string) => {
  return useQuery([cacheKeys.adyenSplitConfiguration, id], () =>
    adyenAccountHoldersHttp.getSplitConfiguration(id)
  );
};

export const useGetPaymentInstruments = (id?: string) => {
  return useQuery([cacheKeys.adyenPaymentInstruments, id], () =>
    adyenAccountHoldersHttp.getPaymentInstruments(id)
  );
};
